import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useKeycloak } from "@react-keycloak/web";
import { useFetchBODemandeInscriptionQuery } from "../../../redux/slice/api/BO/demandeInscriptionApi";
import { useFetchPaysQuery, useFetchSecteurActiviteQuery, useFetchFonctionQuery } from "../../../redux/slice/api/staticData";
import { useLazyFetchUserByLoginQuery } from "../../../redux/slice/api/authApi";
import SpinnerEditeurPro from "../../FO/components/SpinnerEditeurPro";
import InscriptionFormContentBO from "./components/forms/InscriptionFormContentBO";
import { IInscription } from "../../../models/interfaces/IInscription";
import { useInscriptionActions } from "./hooks/useInscriptionActions";
import PriseEnChargeCheckbox from "./components/PriseEnChargeCheckbox";
import RefuseModal from "./components/RefuseModal";
import { ETATS_DEMANDE } from "../../../config/helpers";
import { useSirenValidation } from "../../../hooks/useSirenValidation";
import { useEmailValidation } from "../../../hooks/useEmailValidation";
import { getInscriptionSchemaBO } from "./InscriptionSchemaBO";
import { FRANCE_ET_ASSIMILES_IDS } from "../../../utils/helpers";

const PageBOInscriptionDetail: React.FC = () => {
  const { Id } = useParams<{ Id: string }>();
  const inscriptionId = Number(Id);
  const { keycloak } = useKeycloak();
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | undefined>(undefined);
  const [isFranceOrAssimile, setIsFranceOrAssimile] = useState(false);
  
  // Refs pour les valeurs dynamiques utilisées dans le schema
  const isFranceOrAssimileRef = React.useRef(false);
  const sirenApiErrorRef = React.useRef<string | null>(null);
  const emailApiErrorRef = React.useRef<string | null>(null);
  const previousPaysIdRef = React.useRef<number | undefined>(undefined);
  
  // Utiliser l'API pour récupérer l'utilisateur connecté
  const [callUserByLogin, { data: UserByLoginData }] = useLazyFetchUserByLoginQuery();
  
  // Créer un resolver dynamique qui utilise les refs
  const dynamicResolver = React.useMemo(
    () => async (values: any, context: any, options: any) => {
      const schema = getInscriptionSchemaBO(isFranceOrAssimileRef.current, sirenApiErrorRef.current, emailApiErrorRef.current);
      return yupResolver(schema)(values, context, options);
    },
    []
  );
  
  const methods = useForm<IInscription>({
    mode: "all",
    reValidateMode: "onChange",
    resolver: dynamicResolver as any,
  });
  
  const { watch, setValue, trigger, formState } = methods;
  
  const { data: inscriptionData, isFetching, error, refetch } = useFetchBODemandeInscriptionQuery(
    inscriptionId,
    { 
      skip: !Id || !keycloak.authenticated,
      refetchOnMountOrArgChange: true
    }
  );

  const { data: paysData } = useFetchPaysQuery(undefined, {
    skip: !keycloak.authenticated
  });
  const { data: secteurActivitesData } = useFetchSecteurActiviteQuery(undefined, {
    skip: !keycloak.authenticated
  });
  const { data: fonctionsData } = useFetchFonctionQuery(undefined, {
    skip: !keycloak.authenticated
  });

  const {
    priseEnCharge,
    setPriseEnCharge,
    showRefuseModal,
    setShowRefuseModal,
    explicationRefus,
    setExplicationRefus,
    selectedTypeRefus,
    setSelectedTypeRefus,
    isUpdating,
    isSaving,
    handleSave,
    handleSaveAndContinue,
    handlePriseEnCharge,
    handleRefuser,
    confirmRefuser,
    handleActiverCompte,
    handleDesactiverCompte
  } = useInscriptionActions(inscriptionId, async () => {
    // Callback pour recharger les données depuis l'API
    await refetch();
  });

  // Vérifier si l'utilisateur connecté est celui qui a pris en charge la demande
  const isUserOwner = inscriptionData?.Demandeinscription?.IdUtilisateurPrisEnCharge
    ? currentUserId === inscriptionData.Demandeinscription.IdUtilisateurPrisEnCharge
    : true; // Si pas encore pris en charge, considérer comme autorisé

  // Vérifier si l'utilisateur peut cocher la prise en charge
  const canTogglePriseEnCharge = !priseEnCharge && (!inscriptionData?.Demandeinscription?.IdUtilisateurPrisEnCharge || isUserOwner);

  // Charger les informations de l'utilisateur connecté via l'API
  useEffect(() => {
    if (keycloak.authenticated) {
      keycloak.loadUserInfo().then((userInfo: any) => {
        callUserByLogin(userInfo.preferred_username);
      });
    }
  }, [keycloak.authenticated]);

  // Récupérer l'IdPersonne depuis la réponse de l'API
  useEffect(() => {
    if (UserByLoginData?.Compte?.IdPersonne) {
      setCurrentUserId(UserByLoginData.Compte.IdPersonne);
    }
  }, [UserByLoginData]);

  useEffect(() => {
    if (inscriptionData?.Demandeinscription) {
      // Reset avec shouldValidate: false pour éviter la validation prématurée
      methods.reset(inscriptionData.Demandeinscription, { 
        keepErrors: false,
        keepDirty: false,
        keepValues: false,
        keepDefaultValues: false,
        keepIsSubmitted: false,
        keepTouched: false,
        keepIsValid: false,
        keepSubmitCount: false,
      });
      
      // Attendre que le pays soit détecté avant de valider
      // La validation sera déclenchée par le useEffect qui surveille watchedIdPays
      
      if (inscriptionData.Demandeinscription?.IdEtatDemandeInscription === ETATS_DEMANDE.EN_COURS) {
        setPriseEnCharge(true);
      } else {
        setPriseEnCharge(false);
      }
      setIsInitialized(true);
    } else {
      setIsInitialized(false);
    }
  }, [inscriptionData, methods, setPriseEnCharge]);

  // Surveiller le champ Siren, IdPays et Email
  const watchedSiren = watch("Siren");
  const watchedIdPays = watch("IdPays");
  const watchedEmail = watch("Email");

  // Vérifier si la demande est en cours de traitement
  const isDemandeEnCours = inscriptionData?.Demandeinscription?.IdEtatDemandeInscription === ETATS_DEMANDE.EN_COURS;

  // Utiliser le hook de validation du SIREN (avec toasts uniquement si la demande est en cours)
  const { sirenApiError } = useSirenValidation({
    siren: watchedSiren,
    isFranceOrAssimile,
    idDemande: inscriptionId,
    setValue,
    trigger,
    raisonSocialeSirenFieldName: "RaisonSocialeSiren",
    raisonSocialeFieldName: "RaisonSociale",
    showToasts: isDemandeEnCours // Afficher les toasts et appeler l'API uniquement si la demande est en cours
  });

  // Utiliser le hook de validation de l'email (avec toasts uniquement si la demande est en cours)
  const { emailApiError, checkEmail, isLoading: isCheckingEmail } = useEmailValidation({
    email: watchedEmail,
    idDemande: inscriptionId,
    initialEmail: inscriptionData?.Demandeinscription?.Email || null,
    showToasts: isDemandeEnCours // Afficher les toasts uniquement si la demande est en cours
  });

  // Vérifier si le pays est France ou assimilé
  React.useEffect(() => {
    if (watchedIdPays !== undefined && paysData?.Pays && isInitialized) {
      // Détecter si le pays a changé (pas lors du chargement initial)
      const paysHasChanged = previousPaysIdRef.current !== undefined && previousPaysIdRef.current !== watchedIdPays;
      
      const selectedPays = paysData.Pays.find(p => p.Id === Number(watchedIdPays));
      const isFrance = selectedPays !== undefined && FRANCE_ET_ASSIMILES_IDS.includes(selectedPays.Id);
      const wasFrance = previousPaysIdRef.current !== undefined && paysData.Pays.find(p => p.Id === Number(previousPaysIdRef.current)) !== undefined && FRANCE_ET_ASSIMILES_IDS.includes(paysData.Pays.find(p => p.Id === Number(previousPaysIdRef.current))!.Id);
      
      // Si le pays a changé : vider les champs SIREN seulement si on passe DE France vers non-France
      if (paysHasChanged) {
        if (wasFrance && !isFrance) {
          // Passage de France vers non-France : vider les champs SIREN
          setValue("Siren", "", { shouldValidate: true, shouldTouch: true });
          setValue("RaisonSocialeSiren", "", { shouldValidate: false, shouldTouch: false });
        }
        // Si on passe de non-France vers France, on ne vide pas (l'utilisateur pourra saisir)
      }
      
      // Mettre à jour la référence du pays précédent
      previousPaysIdRef.current = watchedIdPays;
      
      setIsFranceOrAssimile(isFrance);
      isFranceOrAssimileRef.current = isFrance;
      // Re-valider le champ Siren avec le nouveau contexte
      trigger("Siren");
    }
  }, [watchedIdPays, paysData, isInitialized, trigger, setValue]);

  // Mettre à jour la ref sirenApiError et re-valider quand elle change
  React.useEffect(() => {
    sirenApiErrorRef.current = sirenApiError || null;
    if (isInitialized && watchedSiren) {
      // Re-vérifier la validation du champ Siren
      trigger("Siren");
    }
  }, [sirenApiError, isInitialized, watchedSiren, trigger]);

  // Mettre à jour la ref emailApiError et re-valider quand elle change
  React.useEffect(() => {
    emailApiErrorRef.current = emailApiError || null;
    if (isInitialized && watchedEmail) {
      // Re-vérifier la validation du champ Email
      trigger("Email");
    }
  }, [emailApiError, isInitialized, watchedEmail, trigger]);

  return (
    <div className="container text-start">
      <div className="row pt-3 pb-3">
        <div className="col-12">
          <h2 className="titreModule titre-bleu">
            <i className="bi-person-plus pe-2" />
            Détail de la demande d&apos;inscription
          </h2>
        </div>
      </div>

      {/* Prise en charge - Afficher toujours sauf si pas de données */}
      {!keycloak.authenticated || isFetching || !isInitialized ? null : inscriptionData?.Demandeinscription ? (
        <div className="row mb-3">
          <div className="col-12">
            <div className="card" style={{ backgroundColor: "#f8f9fa" }}>
              <div className="card-body">
                <PriseEnChargeCheckbox
                  checked={priseEnCharge}
                  isUpdating={isUpdating}
                  canToggle={canTogglePriseEnCharge}
                  userName={inscriptionData.Demandeinscription.UtilisateurPrisEnCharge}
                  etatDemande={inscriptionData.Demandeinscription.IdEtatDemandeInscription}
                  messageRefus={inscriptionData.Demandeinscription.ExplicationRefus}
                  onChange={handlePriseEnCharge}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {!keycloak.authenticated || isFetching ? (
                <SpinnerEditeurPro />
              ) : error ? (
                <div className="alert alert-danger">
                  Erreur lors du chargement des données
                </div>
              ) : inscriptionData?.Demandeinscription ? (
                <>
                  <FormProvider {...methods}>
                    <InscriptionFormContentBO
                      data={inscriptionData.Demandeinscription}
                      pays={paysData?.Pays || []}
                      secteurActivites={secteurActivitesData?.SecteurActivites || []}
                      fonctions={fonctionsData?.Fonctions || []}
                      readOnly={!priseEnCharge || !isUserOwner}
                      hideButtons={!isUserOwner}
                      onSave={handleSave}
                      onSaveAndContinue={handleSaveAndContinue}
                      onValider={handleActiverCompte}
                      onRefuser={handleRefuser}
                      onDesactiver={handleDesactiverCompte}
                      onAnnuler={() => { /* TODO: Implémenter l'annulation */ }}
                      isUpdating={isUpdating}
                      isSaving={isSaving}
                      sirenApiError={sirenApiError}
                      emailApiError={emailApiError}
                      checkEmail={checkEmail}
                      isCheckingEmail={isCheckingEmail}
                      formState={formState}
                      isFranceOrAssimile={isFranceOrAssimile}
                    />
                  </FormProvider>
                </>
              ) : (
                <div>Aucune donnée disponible</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <RefuseModal
        show={showRefuseModal}
        isUpdating={isUpdating}
        explicationRefus={explicationRefus}
        selectedTypeRefus={selectedTypeRefus?.Id || null}
        onClose={() => setShowRefuseModal(false)}
        onConfirm={confirmRefuser}
        onExplicationChange={setExplicationRefus}
        onTypeRefusChange={setSelectedTypeRefus}
      />
    </div>
  );
};

export default PageBOInscriptionDetail;